import { useState } from 'react'
import {
  Bike, Clock, Flame, MapPin, MessageCircle, Printer, Receipt, Sandwich, ShoppingBag, Store,
} from 'lucide-react'
import { CanalPedido, formatarReais, StatusPedido, TipoEntrega } from '@du/shared'
import {
  Badge, Button, Destaque, Divider, EmptyState, Escolha, GrupoEscolhas, Icone, InfoBand,
  Input, KanbanColumn, LogoMark, MenuRow, Modal, OrderCard, OrderStatusPill, PriceSeal,
  ProductCard, QuantityStepper, SectionTitle, Select, Skeleton, StatTile, Tabs, Textarea,
  useAviso,
} from '@du/ui'

/** Fonte: "Du Lanches/Guia de Marca/Du Lanches Brand.dc.html" — Manual v1.0. */
const CORES = [
  { nome: 'Laranja Dú', hex: '#FF4A00', pantone: '1655 C', uso: 'cor principal · 60%', texto: '#0E0B09' },
  { nome: 'Amarelo Mostarda', hex: '#FFD400', pantone: '109 C', uso: 'preços e destaques · 12%', texto: '#0E0B09' },
  { nome: 'Preto Chapa', hex: '#0E0B09', pantone: 'Black 6 C', uso: 'contraste e faixas · 20%', texto: '#FFFFFF' },
  { nome: 'Creme Pão', hex: '#F3E9DC', pantone: '—', uso: 'FUNDO PADRÃO do app · só digital', texto: '#0E0B09' },
  { nome: 'Branco', hex: '#FFFFFF', pantone: '—', uso: 'títulos sobre laranja · 8%', texto: '#0E0B09' },
]

const CONTRASTES = [
  { par: 'Preto sobre Laranja', razao: '5,82:1', regra: 'AA · corpo e título', ok: true },
  { par: 'Branco sobre Laranja', razao: '3,37:1', regra: 'AA · só título ≥24px', ok: true },
  { par: 'Preto sobre Amarelo', razao: '13,7:1', regra: 'AAA · tudo', ok: true },
  { par: 'Amarelo sobre Preto', razao: '13,7:1', regra: 'AAA · tudo', ok: true },
  { par: 'Branco sobre Preto', razao: '19,6:1', regra: 'AAA · tudo', ok: true },
  { par: 'Preto sobre Creme', razao: '16,3:1', regra: 'AAA · tudo', ok: true },
  { par: 'Amarelo sobre Laranja', razao: '2,35:1', regra: 'REPROVA · nunca usar', ok: false },
  { par: 'Branco sobre Amarelo', razao: '1,43:1', regra: 'REPROVA · nunca usar', ok: false },
  { par: 'Laranja sobre Creme', razao: '2,81:1', regra: 'REPROVA · use Laranja 800', ok: false },
  { par: 'Amarelo sobre Creme', razao: '1,19:1', regra: 'REPROVA · preço vira Laranja 800', ok: false },
  { par: 'Laranja 800 sobre Creme', razao: '5,90:1', regra: 'AA · cor de preço no tema creme', ok: true },
]

const ICONES = [
  { glifo: Sandwich, nome: 'Lanche' }, { glifo: Flame, nome: 'Quentinho' },
  { glifo: ShoppingBag, nome: 'Pedido' }, { glifo: Bike, nome: 'Delivery' },
  { glifo: Clock, nome: 'Horário' }, { glifo: MapPin, nome: 'Endereço' },
  { glifo: Receipt, nome: 'Comanda' }, { glifo: Printer, nome: 'Impressora' },
  { glifo: Store, nome: 'Balcão' }, { glifo: MessageCircle, nome: 'WhatsApp' },
]

export function DesignSystem() {
  const [tema, setTema] = useState<'creme' | 'escuro'>('creme')
  const [qtd, setQtd] = useState(2)
  const [modalAberto, setModalAberto] = useState(false)
  const [aba, setAba] = useState('lanches')
  const [adicionais, setAdicionais] = useState<string[]>(['bacon'])
  const [ponto, setPonto] = useState('ao-ponto')
  const [obs, setObs] = useState('')
  const { avisar } = useAviso()

  const trocarTema = () => {
    const novo = tema === 'creme' ? 'escuro' : 'creme'
    setTema(novo)
    document.documentElement.dataset.tema = novo
  }

  return (
    <div className="pagina">
      <header className="barra-topo">
        <LogoMark largura={200} />
        <div className="linha-flex">
          <Badge tom="destaque">Manual v1.0 · out/2026</Badge>
          <Button variante="secundario" tamanho="sm" onClick={trocarTema}>
            Tema: {tema}
          </Button>
        </div>
      </header>

      <Divider />

      {/* ---------------- 01 CORES ---------------- */}
      <section className="bloco">
        <SectionTitle variante="laranja">01 · Cores</SectionTitle>
        <p style={{ color: 'var(--du-texto-2)' }}>
          Proporção prescrita pelo guia: 60% laranja · 20% preto · 12% amarelo · 8% branco.
        </p>
        <div className="grade grade--3" style={{ marginBlockStart: 'var(--du-esp-4)' }}>
          {CORES.map((c) => (
            <div className="swatch" key={c.hex}>
              <div className="swatch__cor" style={{ background: c.hex, color: c.texto }}>
                {c.hex}
              </div>
              <div className="swatch__meta">
                <b>{c.nome}</b>
                <code>Pantone {c.pantone}</code>
                <div style={{ color: 'var(--du-texto-3)' }}>{c.uso}</div>
              </div>
            </div>
          ))}
        </div>

        <h3 style={{ marginBlockStart: 'var(--du-esp-6)', fontSize: 'var(--du-titulo-sm)', color: 'var(--du-titulo-cor)' }}>
          Contraste medido (WCAG 2.1)
        </h3>
        <p style={{ color: 'var(--du-texto-2)', fontSize: 'var(--du-texto-sm)' }}>
          Três combinações que pareciam naturais reprovam. Os tokens semânticos
          existem para ninguém escolher cor na mão e cair nelas.
        </p>
        <table className="contraste">
          <thead>
            <tr><th>Par</th><th>Razão</th><th>Regra</th></tr>
          </thead>
          <tbody>
            {CONTRASTES.map((c) => (
              <tr key={c.par} className={c.ok ? undefined : 'tr-reprova'}>
                <td>{c.par}</td>
                <td style={{ fontVariantNumeric: 'tabular-nums' }}>{c.razao}</td>
                <td>{c.regra}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* ---------------- 02 TIPOGRAFIA ---------------- */}
      <section className="bloco">
        <SectionTitle variante="amarelo">02 · Tipografia</SectionTitle>
        <div className="grade grade--2">
          <div>
            <div className="rotulo-demo">Títulos · Anton · sempre caixa alta</div>
            <div style={{ fontFamily: 'var(--du-fonte-titulo)', textTransform: 'uppercase', lineHeight: 0.95 }}>
              <div style={{ fontSize: 'var(--du-titulo-lg)' }}>Combos do Dú</div>
              <div style={{ fontSize: 'var(--du-titulo-md)' }}>Lanches artesanais</div>
              <div style={{ fontSize: 'var(--du-titulo-sm)' }}>Porção de fritas</div>
            </div>
          </div>
          <div>
            <div className="rotulo-demo">Textos · Archivo · Regular / Bold / Black</div>
            <p style={{ margin: 0, fontWeight: 400 }}>
              Hambúrguer, presunto, bacon, ovo, alface, queijo, tomate e maionese.
            </p>
            <p style={{ margin: 'var(--du-esp-2) 0 0', fontWeight: 700 }}>
              Av. dos Estudantes, 400 — Centro, Caieiras/SP
            </p>
            <p style={{ margin: 'var(--du-esp-2) 0 0', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Delivery 17h30 às 0h
            </p>
          </div>
        </div>
        <div style={{ marginBlockStart: 'var(--du-esp-5)' }}>
          <div className="rotulo-demo">Destaque · Permanent Marker · máx. 1 por peça</div>
          <Destaque>Promoção da semana!</Destaque>
        </div>
        <p style={{ color: 'var(--du-texto-3)', fontSize: 'var(--du-texto-sm)', marginBlockStart: 'var(--du-esp-4)' }}>
          Hierarquia do guia: DESTAQUE → TÍTULO → TEXTO. Preço sempre {formatarReais(3070)}.
        </p>
      </section>

      {/* ---------------- 03 ÍCONES ---------------- */}
      <section className="bloco">
        <SectionTitle variante="preto">03 · Ícones</SectionTitle>
        <p style={{ color: 'var(--du-texto-2)' }}>
          Traço de 3px em grade 48×48, pontas arredondadas, uma cor só. A razão
          3/48 é mantida em qualquer tamanho: strokeWidth = tamanho ÷ 16.
        </p>
        <div className="grade grade--4" style={{ marginBlockStart: 'var(--du-esp-4)' }}>
          {ICONES.map((i) => (
            <div key={i.nome} style={{ display: 'grid', justifyItems: 'center', gap: 'var(--du-esp-2)' }}>
              <Icone de={i.glifo} tamanho={48} cor="marca" />
              <span style={{ fontSize: 'var(--du-texto-xs)', color: 'var(--du-texto-2)' }}>{i.nome}</span>
            </div>
          ))}
        </div>
        <div className="linha-flex" style={{ marginBlockStart: 'var(--du-esp-5)' }}>
          {[16, 24, 32, 48].map((t) => (
            <div key={t} style={{ display: 'grid', justifyItems: 'center', gap: 4 }}>
              <Icone de={Sandwich} tamanho={t} cor="texto" />
              <span style={{ fontSize: 'var(--du-texto-2xs)', color: 'var(--du-texto-3)' }}>{t}px</span>
            </div>
          ))}
        </div>
      </section>

      {/* ---------------- 04 ELEMENTOS GRÁFICOS ---------------- */}
      <section className="bloco">
        <SectionTitle variante="vazado">04 · Elementos gráficos</SectionTitle>

        <div className="rotulo-demo">Selo de preço · amarelo, borda preta, levemente girado</div>
        <div className="linha-flex">
          <PriceSeal precoCents={5890} tamanho="lg" />
          <PriceSeal precoCents={4990} tamanho="md" rotulo="combo" />
          <PriceSeal precoCents={1590} tamanho="sm" />
          <PriceSeal precoCents={3070} tamanho="md" reto />
        </div>

        <div className="rotulo-demo" style={{ marginBlockStart: 'var(--du-esp-6)' }}>
          Lista de cardápio · pontilhado preto
        </div>
        <div style={{ maxInlineSize: 640 }}>
          <MenuRow nome="X-Tudo" ingredientes="Hambúrguer, presunto, bacon, ovo, alface, queijo, tomate e maionese" precoCents={3070} />
          <MenuRow nome="X-Bacon" ingredientes="Hambúrguer, bacon, alface, queijo e maionese" precoCents={2290} />
          <MenuRow nome="Beirute Contra Filé" ingredientes="Pão sírio, contra filé, queijo, presunto, ovo, cheddar" precoCents={6290} />
          <MenuRow nome="Hot Dog 4" precoCents={2490} esgotado />
        </div>

        <div className="rotulo-demo" style={{ marginBlockStart: 'var(--du-esp-6)' }}>
          Faixa de informação · sempre na base da peça
        </div>
        <InfoBand tom="laranja">
          <span>(11) 9.9702-5250</span>
          <span>Delivery 17h30 às 0h</span>
          <span>Balcão 16h30 às 01h30</span>
        </InfoBand>
        <div style={{ marginBlockStart: 'var(--du-esp-2)' }}>
          <InfoBand tom="preto">
            <span>Av. dos Estudantes, 400 — Centro, Caieiras/SP</span>
          </InfoBand>
        </div>

        <div className="rotulo-demo" style={{ marginBlockStart: 'var(--du-esp-6)' }}>Logo em cada fundo permitido</div>
        <div className="grade grade--3">
          <div className="palco palco--laranja"><LogoMark largura={140} comPlaca={false} /></div>
          <div className="palco palco--preto"><LogoMark largura={140} /></div>
          <div className="palco palco--creme"><LogoMark largura={140} /></div>
        </div>
      </section>

      {/* ---------------- 05 COMPONENTES ---------------- */}
      <section className="bloco">
        <SectionTitle variante="laranja">05 · Componentes</SectionTitle>

        <div className="rotulo-demo">Botões · alvo de toque mínimo de 44px</div>
        <div className="linha-flex">
          <Button variante="primario" icone={ShoppingBag}>Adicionar ao carrinho</Button>
          <Button variante="secundario" icone={Printer}>Reimprimir</Button>
          <Button variante="ghost">Cancelar</Button>
          <Button variante="pilula" icone={MessageCircle}>Peça no WhatsApp</Button>
          <Button variante="perigo">Recusar pedido</Button>
          <Button carregando>Enviando</Button>
          <Button disabled>Indisponível</Button>
        </div>
        <div className="linha-flex" style={{ marginBlockStart: 'var(--du-esp-3)' }}>
          <Button tamanho="sm">Pequeno</Button>
          <Button tamanho="md">Médio</Button>
          <Button tamanho="lg">Grande</Button>
        </div>

        <div className="rotulo-demo" style={{ marginBlockStart: 'var(--du-esp-6)' }}>Etiquetas e status</div>
        <div className="linha-flex">
          <Badge tom="marca">Novo</Badge>
          <Badge tom="destaque">Mais pedido</Badge>
          <Badge tom="neutro">Retirada</Badge>
          <Badge tom="sucesso">Pago</Badge>
          <Badge tom="alerta">Sem ACK</Badge>
          <Badge tom="erro">Esgotado</Badge>
        </div>
        <div className="linha-flex" style={{ marginBlockStart: 'var(--du-esp-3)' }}>
          {Object.values(StatusPedido).map((s) => (
            <OrderStatusPill key={s} status={s} />
          ))}
          <OrderStatusPill status={StatusPedido.EM_PREPARO} atrasadoMin={12} />
        </div>

        <div className="rotulo-demo" style={{ marginBlockStart: 'var(--du-esp-6)' }}>
          Cartão de produto · foto de perto; sem foto, placa laranja (nunca ilustração)
        </div>
        <div className="grade grade--3">
          <ProductCard nome="X-Tudo" precoCents={3070} fotoUrl="/produtos/lanches-x-tudo-800.webp" ingredientes="Hambúrguer, presunto, bacon, ovo, alface, queijo, tomate e maionese" destaque selo />
          <ProductCard nome="X-Bacon" precoCents={2290} fotoUrl="/produtos/lanches-x-bacon-800.webp" ingredientes="Hambúrguer, bacon, alface, queijo e maionese" selo />
          <ProductCard nome="Batata Pequena" precoCents={1590} fotoUrl="/produtos/porcao-de-fritas-pequena-800.webp" ingredientes="Porção pequena de fritas" />
          <ProductCard nome="Hot Dog 4" precoCents={2490} fotoUrl="/produtos/hot-dog-hot-dog-4-800.webp" ingredientes="2 salsichas, milho, bacon, purê, frango desfiado, catupiry" esgotado />
          <ProductCard nome="Beirute Contra Filé" precoCents={6290} ingredientes="Pão sírio, contra filé, queijo, presunto, ovo, cheddar" />
          <ProductCard nome="X-Salada Artesanal" precoCents={3290} ingredientes="Hambúrguer artesanal gourmet 180g, queijo prato, alface, tomate" />
        </div>
        <p style={{ color: 'var(--du-texto-3)', fontSize: 'var(--du-texto-sm)' }}>
          Os dois últimos ainda não têm foto — caem na placa laranja, como o guia manda
          (nunca ilustração genérica). 27 dos 51 itens já têm foto real.
        </p>

        <div className="rotulo-demo" style={{ marginBlockStart: 'var(--du-esp-6)' }}>Quantidade, números do dia e carregamento</div>
        <div className="linha-flex">
          <QuantityStepper valor={qtd} onMudar={setQtd} />
          <span style={{ color: 'var(--du-texto-2)' }}>
            {qtd} × X-Tudo = <b style={{ color: 'var(--du-titulo-cor)' }}>{formatarReais(3070 * qtd)}</b>
          </span>
        </div>
        <div className="grade grade--4" style={{ marginBlockStart: 'var(--du-esp-4)' }}>
          <StatTile rotulo="Pedidos hoje" valor="37" apoio="9 em preparo" tom="marca" />
          <StatTile rotulo="Faturamento" valor={formatarReais(142_690)} apoio="só entregues" tom="destaque" />
          <StatTile rotulo="Ticket médio" valor={formatarReais(3856)} />
          <StatTile rotulo="Tempo médio" valor="38 min" apoio="meta 40" />
        </div>
        <div className="amostra" style={{ marginBlockStart: 'var(--du-esp-4)', maxInlineSize: 360 }}>
          <Skeleton altura={120} />
          <Skeleton altura={20} largura="70%" />
          <Skeleton altura={14} largura="45%" />
        </div>

        <div style={{ marginBlockStart: 'var(--du-esp-5)', border: '1px solid var(--du-borda-cor)', borderRadius: 'var(--du-raio-lg)' }}>
          <EmptyState
            icone={Sandwich}
            titulo="Nenhum pedido agora"
            texto="Assim que entrar pedido, ele aparece aqui com som e destaque."
            acao={<Button variante="secundario" tamanho="sm">Lançar pedido de balcão</Button>}
          />
        </div>
      </section>

      {/* ---------------- 06 FORMULÁRIO ---------------- */}
      <section className="bloco">
        <SectionTitle variante="amarelo">06 · Formulário</SectionTitle>
        <p style={{ color: 'var(--du-texto-2)' }}>
          Campo com 16px de fonte (menos que isso faz o iPhone dar zoom ao focar),
          alvo de toque de 44px, erro ligado por aria-describedby e anunciado.
        </p>
        <div className="grade grade--2" style={{ marginBlockStart: 'var(--du-esp-4)' }}>
          <Input rotulo="Nome" placeholder="Como te chamamos?" required />
          <Input rotulo="WhatsApp" prefixo="+55" placeholder="11 99702-5250" inputMode="tel" required />
          <Input rotulo="CEP" placeholder="07700-000" dica="Buscamos a rua pra você" inputMode="numeric" />
          <Input rotulo="Número" erro="Informe o número da casa" required />
          <Select
            rotulo="Bairro"
            placeholder="Escolha o bairro"
            opcoes={[
              { valor: 'centro', rotulo: 'Centro · R$ 6,00 · 35 min' },
              { valor: 'laranjeiras', rotulo: 'Laranjeiras · R$ 9,00 · 45 min' },
              { valor: 'serpa', rotulo: 'Serpa · R$ 12,00 · 55 min' },
            ]}
            required
          />
          <Select
            rotulo="Pagamento"
            opcoes={[
              { valor: 'dinheiro', rotulo: 'Dinheiro' },
              { valor: 'cartao', rotulo: 'Cartão na entrega' },
              { valor: 'pix', rotulo: 'Pix' },
            ]}
          />
        </div>
        <div style={{ marginBlockStart: 'var(--du-esp-4)', maxInlineSize: 540 }}>
          <Textarea
            rotulo="Observação do pedido"
            placeholder="Sem cebola, ponto da carne, troco..."
            maxLength={200}
            value={obs}
            onChange={(e) => setObs(e.target.value)}
          />
        </div>

        <div style={{ marginBlockStart: 'var(--du-esp-6)', maxInlineSize: 540 }}>
          <GrupoEscolhas nome="Ponto da carne" min={1} max={1} obrigatorio escolhidos={ponto ? 1 : 0}>
            {[
              { v: 'mal', r: 'Mal passado' },
              { v: 'ao-ponto', r: 'Ao ponto' },
              { v: 'bem', r: 'Bem passado' },
            ].map((o) => (
              <Escolha
                key={o.v}
                tipo="radio"
                nome="ponto"
                valor={o.v}
                rotulo={o.r}
                marcada={ponto === o.v}
                onMudar={() => setPonto(o.v)}
              />
            ))}
          </GrupoEscolhas>

          <GrupoEscolhas nome="Adicionais" min={0} max={2} escolhidos={adicionais.length}>
            {[
              { v: 'bacon', r: 'Bacon extra', p: 500 },
              { v: 'ovo', r: 'Ovo', p: 300 },
              { v: 'catupiry', r: 'Catupiry', p: 400, esgotado: true },
            ].map((o) => (
              <Escolha
                key={o.v}
                tipo="checkbox"
                nome="adicionais"
                valor={o.v}
                rotulo={o.r}
                precoExtra={`+ ${formatarReais(o.p)}`}
                esgotada={o.esgotado}
                marcada={adicionais.includes(o.v)}
                onMudar={(m) =>
                  setAdicionais((a) => (m ? [...a, o.v] : a.filter((x) => x !== o.v)))
                }
              />
            ))}
          </GrupoEscolhas>
        </div>
      </section>

      {/* ---------------- 07 NAVEGAÇÃO, AVISO E MODAL ---------------- */}
      <section className="bloco">
        <SectionTitle variante="preto">07 · Navegação, aviso e modal</SectionTitle>
        <Tabs
          itens={[
            { id: 'lanches', rotulo: 'Lanches', contador: 20 },
            { id: 'artesanais', rotulo: 'Artesanais', contador: 4 },
            { id: 'hotdog', rotulo: 'Hot Dog', contador: 4 },
            { id: 'especiais', rotulo: 'Especiais', contador: 5 },
            { id: 'beirutes', rotulo: 'Beirutes', contador: 5 },
            { id: 'combos', rotulo: 'Combos do Dú', contador: 7 },
            { id: 'fritas', rotulo: 'Fritas', contador: 6 },
          ]}
          ativo={aba}
          aoTrocar={setAba}
        />
        <div className="linha-flex" style={{ marginBlockStart: 'var(--du-esp-4)' }}>
          <Button variante="secundario" onClick={() => avisar({ tom: 'sucesso', texto: 'Pedido #042 aceito', detalhe: 'Comanda enviada para a impressora Caixa' })}>
            Aviso de sucesso
          </Button>
          <Button variante="secundario" onClick={() => avisar({ tom: 'alerta', texto: 'Impressora sem resposta', detalhe: 'Tentativa 2 de 5' })}>
            Aviso de alerta
          </Button>
          <Button variante="secundario" onClick={() => avisar({ tom: 'erro', texto: 'Comanda não saiu', detalhe: 'Agente offline há 3 min — não some sozinho' })}>
            Aviso de erro
          </Button>
          <Button onClick={() => setModalAberto(true)}>Abrir modal</Button>
        </div>
        <Modal
          aberto={modalAberto}
          aoFechar={() => setModalAberto(false)}
          titulo="Cancelar pedido #042"
          folha
          rodape={
            <>
              <Button variante="ghost" onClick={() => setModalAberto(false)}>Voltar</Button>
              <Button variante="perigo" onClick={() => { setModalAberto(false); avisar({ tom: 'erro', texto: 'Pedido #042 cancelado' }) }}>
                Cancelar pedido
              </Button>
            </>
          }
        >
          <p style={{ marginBlockStart: 0, color: 'var(--du-texto-2)' }}>
            O cliente recebe aviso no WhatsApp. O motivo fica registrado no histórico do pedido.
          </p>
          <Textarea rotulo="Motivo" required placeholder="Produto em falta, endereço fora de área..." maxLength={200} />
        </Modal>
      </section>

      {/* ---------------- 08 KANBAN ---------------- */}
      <section className="bloco">
        <SectionTitle variante="laranja">08 · Kanban da cozinha</SectionTitle>
        <p style={{ color: 'var(--du-texto-2)' }}>
          Feito para ser lido a um metro, de relance: número grande, cronômetro
          sempre visível, pedido novo pulsando e atraso mudando a borda inteira.
        </p>
        <div style={{ display: 'flex', gap: 'var(--du-esp-4)', overflowX: 'auto', paddingBlock: 'var(--du-esp-4)' }}>
          <KanbanColumn titulo="Novos" quantidade={1} tom="novo">
            <OrderCard
              numeroDia={42} status={StatusPedido.RECEBIDO} canal={CanalPedido.SITE}
              tipo={TipoEntrega.ENTREGA} clienteNome="Bruno Irapuã" bairroNome="Centro"
              minutosDesde={1} totalCents={10730}
              itens={[{ nome: 'X-Tudo', quantidade: 2 }, { nome: 'Batata cheddar M', quantidade: 1 }]}
            />
          </KanbanColumn>
          <KanbanColumn titulo="Em preparo" quantidade={2} tom="ativo">
            <OrderCard
              numeroDia={41} status={StatusPedido.EM_PREPARO} canal={CanalPedido.WHATSAPP}
              tipo={TipoEntrega.RETIRADA} clienteNome="Marcos Vinícius"
              minutosDesde={18} atrasadoMin={6} totalCents={4390}
              itens={[{ nome: 'Combo X-Bacon', quantidade: 1 }]}
            />
            <OrderCard
              numeroDia={40} status={StatusPedido.EM_PREPARO} canal={CanalPedido.BALCAO}
              tipo={TipoEntrega.BALCAO} clienteNome="Mesa 3"
              minutosDesde={9} totalCents={6290}
              itens={[{ nome: 'Beirute Contra Filé', quantidade: 1 }, { nome: 'X-Egg', quantidade: 1 }, { nome: 'Fritas G', quantidade: 1 }, { nome: 'Coca 600', quantidade: 2 }]}
            />
          </KanbanColumn>
          <KanbanColumn titulo="Prontos" quantidade={1} tom="pronto">
            <OrderCard
              numeroDia={39} status={StatusPedido.PRONTO} canal={CanalPedido.IA}
              tipo={TipoEntrega.ENTREGA} clienteNome="Ana Paula" bairroNome="Laranjeiras"
              minutosDesde={32} totalCents={8940}
              itens={[{ nome: 'X-da Casa', quantidade: 2 }]}
            />
          </KanbanColumn>
          <KanbanColumn titulo="Saiu" quantidade={0}>
            <EmptyState titulo="Ninguém na rua" texto="Pedido pronto aparece aqui ao sair." />
          </KanbanColumn>
        </div>
      </section>

      <InfoBand tom="preto">
        <span>Dú Lanches · Manual de marca v1.0 · outubro 2026</span>
      </InfoBand>
    </div>
  )
}
